import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getProductById, getRelatedProducts } from '../services/productsService';

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cantidad, setCantidad] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [addingToCart, setAddingToCart] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    loadProduct();
  }, [id]);

  const loadProduct = async () => {
    try {
      setLoading(true);
      const productData = await getProductById(id);
      
      if (!productData) {
        setMessage({ text: 'Producto no encontrado', isError: true });
        setTimeout(() => navigate('/shop'), 2000);
        return;
      }
      
      setProduct(productData);
      const related = await getRelatedProducts(id, 4);
      setRelatedProducts(related);
      
    } catch (error) {
      console.error('Error al cargar producto:', error);
      setMessage({ text: 'Error al cargar el producto', isError: true });
    } finally {
      setLoading(false);
    }
  };

  // Carrito local (localStorage) — mismo mecanismo que usaba ShopPage antes
  const handleAddToCart = () => {
    if (!product) return;
    
    if (cantidad > product.stock) {
      setMessage({ text: 'No hay suficiente stock disponible', isError: true });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    setAddingToCart(true);
    try {
      const localCart = JSON.parse(localStorage.getItem('cart') || '[]');
      const existingItem = localCart.find(item => item.producto_id === product.id);
      
      if (existingItem) {
        existingItem.cantidad += cantidad;
      } else {
        localCart.push({
          producto_id: product.id,
          cantidad: cantidad,
          producto: {
            nombre: product.nombre,
            precio: product.precio_descuento || product.precio,
            imagen_url: product.imagen_principal_url
          }
        });
      }
      
      localStorage.setItem('cart', JSON.stringify(localCart));
      
      // Disparar evento para que otros componentes (Header, CartPage) se actualicen
      window.dispatchEvent(new Event('cart-updated'));
      
      setMessage({ text: '✅ Producto agregado al carrito', isError: false });
      setTimeout(() => setMessage(null), 3000);
      
    } catch (error) {
      console.error('Error al agregar al carrito:', error);
      setMessage({ text: 'Error al agregar al carrito', isError: true });
      setTimeout(() => setMessage(null), 3000);
    } finally {
      setAddingToCart(false);
    }
  };

  const getImageUrl = (img) => {
    if (!img) return `${process.env.PUBLIC_URL}/contenido-no-disponible.jpeg`;
    if (img.startsWith('http')) return img;
    return process.env.PUBLIC_URL ? `${process.env.PUBLIC_URL}${img}` : img;
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0
    }).format(price);
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Cargando producto...</div>
      </div>
    );
  }

  if (!product) {
    return (
      <div style={styles.container}>
        <div style={styles.error}>Producto no encontrado</div>
      </div>
    );
  }

  const images = product.imagenes_adicionales && product.imagenes_adicionales.length > 0
    ? [product.imagen_principal_url, ...product.imagenes_adicionales]
    : [product.imagen_principal_url];

  const displayPrice = product.precio_descuento || product.precio;
  const hasDiscount = product.precio_descuento && product.precio_descuento < product.precio;

  return (
    <div style={styles.container}>
      {/* Breadcrumb */}
      <div style={styles.breadcrumb}>
        <span style={styles.breadcrumbLink} onClick={() => navigate('/')}>Inicio</span>
        <span style={styles.breadcrumbSeparator}>/</span>
        <span style={styles.breadcrumbLink} onClick={() => navigate('/shop')}>Tienda</span>
        <span style={styles.breadcrumbSeparator}>/</span>
        <span style={styles.breadcrumbCurrent}>{product.nombre}</span>
      </div>

      {/* Mensaje */}
      {message && (
        <div style={{
          ...styles.message,
          background: message.isError ? '#ffebee' : '#e8f5e9',
          color: message.isError ? '#c62828' : '#2e7d32'
        }}>
          {message.text}
        </div>
      )}

      {/* Contenido principal */}
      <div style={styles.productContainer}>
        {/* Columna izquierda - Imágenes */}
        <div style={styles.imageSection}>
          <div style={styles.mainImageContainer}>
            <img
              src={getImageUrl(images[selectedImage])}
              alt={product.nombre}
              style={styles.mainImage}
              onError={(e) => {
                e.target.src = `${process.env.PUBLIC_URL}/contenido-no-disponible.jpeg`;
              }}
            />
            {hasDiscount && (
              <div style={styles.discountBadge}>
                -{Math.round((1 - product.precio_descuento / product.precio) * 100)}%
              </div>
            )}
          </div>

          {images.length > 1 && (
            <div style={styles.thumbnailContainer}>
              {images.map((img, index) => (
                <img
                  key={index}
                  src={getImageUrl(img)}
                  alt={`${product.nombre} - ${index + 1}`}
                  style={{
                    ...styles.thumbnail,
                    border: selectedImage === index ? '2px solid #dc2626' : '2px solid #e0e0e0'
                  }}
                  onClick={() => setSelectedImage(index)}
                  onError={(e) => {
                    e.target.src = `${process.env.PUBLIC_URL}/contenido-no-disponible.jpeg`;
                  }}
                />
              ))}
            </div>
          )}
        </div>

        {/* Columna derecha - Información */}
        <div style={styles.infoSection}>
          <h1 style={styles.productName}>{product.nombre}</h1>
          
          <div style={styles.priceContainer}>
            {hasDiscount && (
              <span style={styles.originalPrice}>{formatPrice(product.precio)}</span>
            )}
            <span style={styles.currentPrice}>{formatPrice(displayPrice)}</span>
          </div>

          <div style={styles.stockInfo}>
            {product.stock > 0 ? (
              <span style={styles.inStock}>
                ✓ En stock ({product.stock} {product.stock === 1 ? 'unidad' : 'unidades'})
              </span>
            ) : (
              <span style={styles.outOfStock}>✕ Sin stock</span>
            )}
          </div>

          {product.descripcion && (
            <div style={styles.description}>
              <h3 style={styles.sectionTitle}>Descripción</h3>
              <p style={styles.descriptionText}>{product.descripcion}</p>
            </div>
          )}

          {/* Selector de cantidad y botón agregar */}
          {product.stock > 0 && (
            <div style={styles.purchaseSection}>
              <div style={styles.quantitySelector}>
                <label style={styles.quantityLabel}>Cantidad:</label>
                <div style={styles.quantityControls}>
                  <button
                    style={styles.quantityButton}
                    onClick={() => setCantidad(Math.max(1, cantidad - 1))}
                    disabled={cantidad <= 1}
                  >
                    −
                  </button>
                  <input
                    type="number"
                    value={cantidad}
                    onChange={(e) => {
                      const val = parseInt(e.target.value) || 1;
                      setCantidad(Math.min(product.stock, Math.max(1, val)));
                    }}
                    style={styles.quantityInput}
                    min="1"
                    max={product.stock}
                  />
                  <button
                    style={styles.quantityButton}
                    onClick={() => setCantidad(Math.min(product.stock, cantidad + 1))}
                    disabled={cantidad >= product.stock}
                  >
                    +
                  </button>
                </div>
              </div>

              <button
                style={{
                  ...styles.addToCartButton,
                  opacity: addingToCart ? 0.6 : 1,
                  cursor: addingToCart ? 'not-allowed' : 'pointer'
                }}
                onClick={handleAddToCart}
                disabled={addingToCart}
              >
                {addingToCart ? 'Agregando...' : '🛒 Agregar al carrito'}
              </button>
            </div>
          )}

          {/* Información adicional */}
          <div style={styles.additionalInfo}>
            <div style={styles.infoItem}>
              <strong>SKU:</strong> {product.sku || 'N/A'}
            </div>
            <div style={styles.infoItem}>
              <strong>Categoría:</strong> {product.categoria?.nombre || 'Sin categoría'}
            </div>
          </div>
        </div>
      </div>

      {/* Productos relacionados */}
      {relatedProducts.length > 0 && (
        <div style={styles.relatedSection}>
          <h2 style={styles.relatedTitle}>Productos relacionados</h2>
          <div style={styles.relatedGrid}>
            {relatedProducts.map(related => (
              <div
                key={related.id}
                style={styles.relatedCard}
                onClick={() => {
                  navigate(`/product/${related.id}`);
                  window.scrollTo(0, 0);
                }}
              >
                <img
                  src={getImageUrl(related.imagen_principal_url)}
                  alt={related.nombre}
                  style={styles.relatedImage}
                  onError={(e) => {
                    e.target.src = `${process.env.PUBLIC_URL}/contenido-no-disponible.jpeg`;
                  }}
                />
                <div style={styles.relatedInfo}>
                  <h4 style={styles.relatedName}>{related.nombre}</h4>
                  <p style={styles.relatedPrice}>{formatPrice(related.precio)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: { maxWidth: '1200px', margin: '0 auto', padding: '24px' },
  loading: { textAlign: 'center', padding: '60px 20px', fontSize: '18px', color: '#666' },
  error: { textAlign: 'center', padding: '60px 20px', fontSize: '18px', color: '#c62828' },
  breadcrumb: { marginBottom: '24px', fontSize: '14px', color: '#666' },
  breadcrumbLink: { color: '#dc2626', cursor: 'pointer', textDecoration: 'none' },
  breadcrumbSeparator: { margin: '0 8px', color: '#999' },
  breadcrumbCurrent: { color: '#333' },
  message: { padding: '12px 16px', borderRadius: '8px', marginBottom: '20px', fontWeight: 600 },
  productContainer: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '48px', marginBottom: '60px' },
  imageSection: { display: 'flex', flexDirection: 'column', gap: '16px' },
  mainImageContainer: { position: 'relative', width: '100%', paddingBottom: '100%', background: '#f5f5f5', borderRadius: '12px', overflow: 'hidden' },
  mainImage: { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'contain' },
  discountBadge: { position: 'absolute', top: '16px', right: '16px', background: '#dc2626', color: 'white', padding: '8px 12px', borderRadius: '8px', fontWeight: 700, fontSize: '14px' },
  thumbnailContainer: { display: 'flex', gap: '12px', flexWrap: 'wrap' },
  thumbnail: { width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s' },
  infoSection: { display: 'flex', flexDirection: 'column', gap: '24px' },
  productName: { fontSize: '32px', fontWeight: 700, color: '#111', margin: 0 },
  priceContainer: { display: 'flex', alignItems: 'center', gap: '12px' },
  originalPrice: { fontSize: '20px', color: '#999', textDecoration: 'line-through' },
  currentPrice: { fontSize: '32px', fontWeight: 700, color: '#dc2626' },
  stockInfo: { fontSize: '16px' },
  inStock: { color: '#2e7d32', fontWeight: 600 },
  outOfStock: { color: '#c62828', fontWeight: 600 },
  description: { borderTop: '1px solid #e0e0e0', paddingTop: '24px' },
  sectionTitle: { fontSize: '18px', fontWeight: 700, marginBottom: '12px' },
  descriptionText: { fontSize: '16px', lineHeight: '1.6', color: '#555', whiteSpace: 'pre-line' },
  purchaseSection: { display: 'flex', flexDirection: 'column', gap: '16px', borderTop: '1px solid #e0e0e0', paddingTop: '24px' },
  quantitySelector: { display: 'flex', alignItems: 'center', gap: '16px' },
  quantityLabel: { fontSize: '16px', fontWeight: 600 },
  quantityControls: { display: 'flex', alignItems: 'center', gap: '8px' },
  quantityButton: { width: '40px', height: '40px', border: '1px solid #ddd', background: 'white', borderRadius: '8px', fontSize: '20px', cursor: 'pointer', transition: 'all 0.2s' },
  quantityInput: { width: '60px', height: '40px', border: '1px solid #ddd', borderRadius: '8px', textAlign: 'center', fontSize: '16px' },
  addToCartButton: { width: '100%', padding: '16px', background: 'transparent', color: '#dc2626', border: '2px solid #dc2626', borderRadius: '12px', fontSize: '18px', fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s' },
  additionalInfo: { display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '14px', color: '#666', borderTop: '1px solid #e0e0e0', paddingTop: '24px' },
  infoItem: { display: 'flex', gap: '8px' },
  relatedSection: { marginTop: '60px' },
  relatedTitle: { fontSize: '24px', fontWeight: 700, marginBottom: '24px' },
  relatedGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '24px' },
  relatedCard: { cursor: 'pointer', transition: 'transform 0.2s', borderRadius: '12px', overflow: 'hidden', border: '1px solid #e0e0e0' },
  relatedImage: { width: '100%', height: '200px', objectFit: 'cover' },
  relatedInfo: { padding: '16px' },
  relatedName: { fontSize: '14px', fontWeight: 600, margin: '0 0 8px 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  relatedPrice: { fontSize: '16px', fontWeight: 700, color: '#dc2626', margin: 0 }
};

export default ProductDetailPage;